/**
 * Site metadata helpers for per-route head management (audit UIUX-02).
 *
 * The share sheet targets Telegram/WhatsApp/LINE/WeChat/SMS - all preview-driven
 * chat apps that scrape the SSR HTML. Without per-entity og:/twitter: tags,
 * every shared class/studio/instructor link previews as a bare URL with the
 * generic site title, killing the share growth loop.
 */
import { getEnvVar } from '@/lib/config/api.config';
import { ASSET_PATHS, getAssetUrl } from '@/lib/utils/assets';

/**
 * Canonical app origin for og:url. Deriving it from VITE_APP_ENV (baked at
 * build time) keeps the value stable per deployment, which matches the CDN's
 * per-route SSR cache: every cached document for a URL carries the correct
 * origin for that deployment.
 */
export function getSiteOrigin(): string {
  return getEnvVar('VITE_APP_ENV', 'development') === 'production'
    ? 'https://app.til.sg'
    : 'https://stg-app.til.sg';
}

export interface EntityHeadInfo {
  /** Tab/preview title, e.g. "Hip Hop Fundamentals - Class | TIL". */
  title: string;
  /** Preview description (entity blurb, or a type-specific fallback). */
  description: string;
  /** Absolute preview image URL; falls back to the brand backdrop. */
  image?: string;
  /** Canonical path, e.g. /class/123. */
  path: string;
}

/**
 * Route head tags for an entity detail page: tab title plus Open Graph and
 * Twitter card tags so chat-app link previews show the entity, not the bare URL.
 *
 * Also emits the canonical link. Without it, every URL shape that resolves
 * (/studio/{uuid} and /studio/{slug}) returns 200 with a different og:url, and
 * crawlers index both as separate pages. Callers must pass the slug form as
 * `path`, never the route param: the param may be a UUID on an old link.
 */
export function entityHeadMeta({ title, description, image, path }: EntityHeadInfo) {
  const ogImage = image || getAssetUrl(ASSET_PATHS.SINGAPORE_BACKDROP);
  const url = `${getSiteOrigin()}${path}`;
  return {
    meta: [
      { title },
      { name: 'description', content: description },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: url },
      { property: 'og:image', content: ogImage },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
      { name: 'twitter:image', content: ogImage },
    ],
    links: [{ rel: 'canonical', href: url }],
  };
}
